import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Database, Download, Trash2, Loader2, HardDriveUpload, Cloud, RefreshCw,
  Plus, Snowflake, Server, KeyRound, ExternalLink, ArchiveRestore, CalendarClock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGIONS = ["blr1", "sgp1", "nyc3", "sfo3", "ams3", "fra1", "syd1"];

export default function BackupsArchives() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [servers, setServers] = useState([]);
  const [serverId, setServerId] = useState("");
  const [archives, setArchives] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [newDb, setNewDb] = useState("");

  const [spaces, setSpaces] = useState({ configured: false });
  const [objects, setObjects] = useState([]);
  const [cfgForm, setCfgForm] = useState({ access_key: "", secret_key: "", region: "blr1", bucket: "" });
  const [restore, setRestore] = useState(null); // {filename, db, running}
  const [schedule, setSchedule] = useState(null);
  const [schedDb, setSchedDb] = useState("");

  const loadArchives = useCallback(async (sid) => {
    if (!sid) return;
    const [a, d] = await Promise.all([
      axios.get(`${API}/backups/${sid}/archives`),
      axios.get(`${API}/backups/${sid}/databases`),
    ]);
    setArchives(a.data); setDatabases(d.data);
  }, []);

  const loadSpaces = useCallback(async () => {
    const s = (await axios.get(`${API}/backups/spaces/config`)).data;
    setSpaces(s);
    if (s.configured) {
      try { setObjects((await axios.get(`${API}/backups/spaces/objects`)).data); } catch { setObjects([]); }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = (await axios.get(`${API}/backups/servers`)).data;
        setServers(s);
        if (s.length) { setServerId(s[0].id); await loadArchives(s[0].id); }
        await loadSpaces();
        try {
          const sc = (await axios.get(`${API}/backups/schedule`)).data;
          setSchedule(sc); setSchedDb(sc.db_name || "");
        } catch {}
      } catch { toast.error("Failed to load backups"); }
      finally { setLoading(false); }
    })();
  }, [loadArchives, loadSpaces]);

  const onServerChange = async (sid) => { setServerId(sid); setLoading(true); try { await loadArchives(sid); } finally { setLoading(false); } };

  const download = async (name) => {
    setBusy("dl:" + name);
    try {
      const { token } = (await axios.get(`${API}/backups/${serverId}/download-token/${encodeURIComponent(name)}`)).data;
      // Browser streams straight to disk (no in-memory buffering — safe for multi-GB dumps)
      window.location.href = `${API}/backups/download-file?token=${encodeURIComponent(token)}`;
      toast.success("Download started");
    } catch { toast.error("Download failed"); } finally { setTimeout(() => setBusy(""), 1500); }
  };

  const createBackup = async () => {
    if (!newDb) return;
    setBusy("create");
    try {
      const res = await axios.post(`${API}/backups/${serverId}/create`, { db_name: newDb });
      const fname = res.data.filename;
      toast.info("Backup started — this can take a while for large databases…");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const s = (await axios.get(`${API}/backups/${serverId}/create-status`, { params: { filename: fname } })).data;
          if (s.status === "done") { clearInterval(poll); setBusy(""); toast.success("Backup complete"); loadArchives(serverId); }
          else if (s.status === "failed") { clearInterval(poll); setBusy(""); toast.error("Backup failed: " + (s.error || "see server")); }
          else if (tries > 60) { clearInterval(poll); setBusy(""); toast.warning("Still running — check the list shortly"); loadArchives(serverId); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(""); }
  };

  const delArchive = async (name) => {
    if (!window.confirm(`Delete ${name} from the server? (Download it first if unsure)`)) return;
    setBusy("del:" + name);
    try { await axios.delete(`${API}/backups/${serverId}/archives/${encodeURIComponent(name)}`); toast.success("Deleted"); loadArchives(serverId); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  const uploadToSpaces = async (name, storage_class) => {
    setBusy("up:" + name);
    try {
      const res = await axios.post(`${API}/backups/${serverId}/upload-to-spaces`, { filename: name, storage_class });
      toast.success(res.data.message);
      loadSpaces();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(""); }
  };

  const saveSpacesCfg = async () => {
    setBusy("cfg");
    try { const r = await axios.post(`${API}/backups/spaces/config`, cfgForm); toast.success(`Connected to ${r.data.bucket}`); loadSpaces(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); } finally { setBusy(""); }
  };

  const removeSpacesCfg = async () => {
    if (!window.confirm("Disconnect DigitalOcean Spaces?")) return;
    await axios.delete(`${API}/backups/spaces/config`); toast.success("Disconnected"); setObjects([]); loadSpaces();
  };

  const presign = async (key) => {
    try { const r = await axios.post(`${API}/backups/spaces/presign`, { key }); window.open(r.data.url, "_blank"); }
    catch { toast.error("Could not generate link"); }
  };

  const startRestore = async () => {
    setBusy("restore");
    try {
      await axios.post(`${API}/backups/${serverId}/restore`, { filename: restore.filename, new_db_name: restore.db });
      setRestore(r => ({ ...r, running: true }));
      toast.info("Restore started — this can take a while for large dumps…");
      const poll = setInterval(async () => {
        try {
          const s = (await axios.get(`${API}/backups/${serverId}/restore-status`, { params: { db_name: restore.db } })).data;
          if (s.status === "done") {
            clearInterval(poll); setBusy(""); setRestore(null);
            toast.success(`Restored into new database "${restore.db}" — ready for audits via psql`);
            loadArchives(serverId);
          } else if (s.status === "failed") {
            clearInterval(poll); setBusy(""); setRestore(r => ({ ...r, running: false, error: s.error }));
            toast.error("Restore failed: " + (s.error || "see server logs"));
          }
        } catch {}
      }, 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed to start"); setBusy(""); }
  };

  const saveSchedule = async (enabled, dbName) => {
    setBusy("sched");
    try {
      const { data } = await axios.put(`${API}/backups/schedule`, {
        enabled, server_id: serverId, db_name: dbName, keep_last: schedule?.keep_last || 8 });
      setSchedule(data);
      toast.success(enabled ? "Weekly backup ON — every Sunday 02:30 IST to Spaces cold storage" : "Weekly backup off");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const runScheduleNow = async () => {
    setBusy("schedrun");
    try {
      const { data } = await axios.post(`${API}/backups/schedule/run-now`);
      toast.info(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const delObject = async (key) => {
    if (!window.confirm(`Delete ${key} from Spaces?`)) return;
    await axios.delete(`${API}/backups/spaces/objects`, { data: { key } }); toast.success("Deleted"); loadSpaces();
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl" data-testid="backups-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Database className="w-6 h-6 text-emerald-400" /> Backups &amp; Archives
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Create and download database archives, and store them in DigitalOcean Spaces.</p>
      </div>

      {servers.length > 1 && (
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-zinc-500" />
          <Select value={serverId} onValueChange={onServerChange}>
            <SelectTrigger className="w-72 bg-black/40 border-white/10 text-white text-sm" data-testid="backup-server-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10 text-white">
              {servers.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.ip_address})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* On-server archives */}
      <Card className="bg-[#0c0c0e] border-white/5" data-testid="onserver-archives-card">
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white font-mono flex items-center gap-2"><HardDriveUpload className="w-5 h-5 text-emerald-400" /> On-server archives</CardTitle>
            <CardDescription className="text-zinc-400">pg_dump files at /var/backups/statutory</CardDescription>
          </div>
          <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => loadArchives(serverId)} data-testid="refresh-archives-btn"><RefreshCw className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div> :
            archives.length === 0 ? <p className="text-sm text-zinc-500">No archives yet. Create one below.</p> :
            archives.map(a => (
              <div key={a.name} className="flex items-center gap-3 px-3 py-2 bg-white/5 border border-white/10 rounded-lg" data-testid={`archive-row-${a.name}`}>
                <Database className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{a.name}</p>
                  <p className="text-[10px] text-zinc-500">{a.size_human} · {a.modified.replace("T", " ")}</p>
                </div>
                <Button size="sm" className="h-7 text-xs bg-emerald-700 hover:bg-emerald-600" disabled={busy === "dl:" + a.name} onClick={() => download(a.name)} data-testid={`download-${a.name}`}>
                  {busy === "dl:" + a.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />Download</>}
                </Button>
                {spaces.configured && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-white/10 text-white" disabled={busy === "up:" + a.name} onClick={() => uploadToSpaces(a.name, "STANDARD")} data-testid={`upload-std-${a.name}`}>
                      {busy === "up:" + a.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Cloud className="w-3 h-3 mr-1" />Spaces</>}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-cyan-500/30 text-cyan-300" disabled={busy === "up:" + a.name} onClick={() => uploadToSpaces(a.name, "COLD")} data-testid={`upload-cold-${a.name}`} title="Cold storage (cheapest, for long-term archives)">
                      <Snowflake className="w-3 h-3 mr-1" />Cold
                    </Button>
                  </>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs border-purple-500/30 text-purple-300"
                          onClick={() => setRestore({ filename: a.name, db: `restore_${a.name.split("_")[0].replace(/[^a-zA-Z0-9]/g, "_").toLowerCase()}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`, running: false })}
                          data-testid={`restore-${a.name}`} title="Restore this dump into a NEW database for audits">
                    <ArchiveRestore className="w-3 h-3 mr-1" />Restore
                  </Button>
                )}
                {isAdmin && <button className="text-zinc-500 hover:text-red-400 p-1" disabled={busy === "del:" + a.name} onClick={() => delArchive(a.name)} data-testid={`delete-${a.name}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))
          }
          <div className="flex items-center gap-2 pt-2 border-t border-white/5">
            <Select value={newDb} onValueChange={setNewDb}>
              <SelectTrigger className="w-64 bg-black/40 border-white/10 text-white text-sm" data-testid="new-backup-db-select"><SelectValue placeholder="Choose a database to back up" /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-white">
                {databases.map(d => <SelectItem key={d.name} value={d.name}>{d.name} ({d.size_human})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-9 bg-emerald-700 hover:bg-emerald-600" disabled={!newDb || busy === "create"} onClick={createBackup} data-testid="create-backup-btn">
              {busy === "create" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Create backup
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Weekly scheduled backup */}
      <Card className="bg-[#0c0c0e] border-white/5" data-testid="schedule-card">
        <CardHeader>
          <CardTitle className="text-white font-mono flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-amber-400" /> Weekly auto-backup
            {schedule?.enabled && <span className="text-[10px] bg-emerald-500/15 text-emerald-300 rounded-full px-2 py-0.5">ON</span>}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {schedule?.schedule_human || "Every Sunday 02:30 IST → Spaces cold storage"} · keeps last {schedule?.keep_last || 8} copies
            {schedule?.last_run && <> · last run {new Date(schedule.last_run).toLocaleString()} ({schedule.last_status})</>}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Select value={schedDb} onValueChange={setSchedDb}>
            <SelectTrigger className="w-64 bg-black/40 border-white/10 text-white text-sm" data-testid="schedule-db-select"><SelectValue placeholder="Database to auto-back-up" /></SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10 text-white">
              {databases.map(d => <SelectItem key={d.name} value={d.name}>{d.name} ({d.size_human})</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch checked={schedule?.enabled || false} disabled={busy === "sched" || !isAdmin || (!schedDb && !schedule?.enabled)}
                    onCheckedChange={v => saveSchedule(v, schedDb || schedule?.db_name)} data-testid="schedule-toggle" />
            <span className="text-xs text-zinc-400">{schedule?.enabled ? `Backing up ${schedule.db_name} weekly` : "Enable weekly backup"}</span>
          </div>
          {schedule?.enabled && isAdmin && (
            <Button size="sm" variant="outline" className="h-8 text-xs border-amber-500/30 text-amber-300"
                    disabled={busy === "schedrun"} onClick={runScheduleNow} data-testid="schedule-run-now-btn">
              {busy === "schedrun" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Run now"}
            </Button>
          )}
          {!spaces.configured && <p className="text-[11px] text-amber-300/80 w-full">Connect DigitalOcean Spaces below first — weekly dumps upload straight to cold storage.</p>}
        </CardContent>
      </Card>

      {/* Restore dialog */}
      {restore && (
        <Dialog open onOpenChange={v => { if (!v && !restore.running) setRestore(null); }}>
          <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-md" data-testid="restore-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ArchiveRestore className="w-5 h-5 text-purple-400" /> Restore into a new database
              </DialogTitle>
              <DialogDescription>
                Restores <span className="font-mono text-purple-300">{restore.filename}</span> into a brand-new database.
                Existing databases are never touched. Use for audits and inspection.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-500 text-xs">New database name</Label>
                <Input value={restore.db} disabled={restore.running}
                       onChange={e => setRestore(r => ({ ...r, db: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") }))}
                       className="bg-black/40 border-white/10 text-white font-mono text-sm" data-testid="restore-db-input" />
              </div>
              {restore.error && <p className="text-xs text-red-400 bg-red-500/10 rounded p-2">{restore.error}</p>}
              {restore.running ? (
                <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 rounded p-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Restoring… large dumps can take 10-30 minutes. You can close this — a toast will confirm.
                </div>
              ) : (
                <Button onClick={startRestore} disabled={busy === "restore" || !/^[a-zA-Z][a-zA-Z0-9_]{2,40}$/.test(restore.db)}
                        className="w-full bg-purple-600 hover:bg-purple-700" data-testid="restore-start-btn">
                  {busy === "restore" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start restore"}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* DigitalOcean Spaces */}
      <Card className="bg-[#0c0c0e] border-white/5" data-testid="spaces-card">
        <CardHeader>
          <CardTitle className="text-white font-mono flex items-center gap-2"><Cloud className="w-5 h-5 text-blue-400" /> DigitalOcean Spaces</CardTitle>
          <CardDescription className="text-zinc-400">Off-server object storage. Use <b>Cold</b> for cheap long-term (7-year) archives.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!spaces.configured ? (
            <div className="space-y-3">
              <p className="text-xs text-amber-300/80">Create a key in DigitalOcean → API → <b>Spaces Keys</b>, and a bucket, then connect it here.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-zinc-500 text-xs">Access Key</Label><Input className="bg-black/40 border-white/10 text-white text-sm" value={cfgForm.access_key} onChange={e => setCfgForm({ ...cfgForm, access_key: e.target.value })} data-testid="spaces-access-key" /></div>
                <div><Label className="text-zinc-500 text-xs">Secret Key</Label><Input type="password" className="bg-black/40 border-white/10 text-white text-sm" value={cfgForm.secret_key} onChange={e => setCfgForm({ ...cfgForm, secret_key: e.target.value })} data-testid="spaces-secret-key" /></div>
                <div><Label className="text-zinc-500 text-xs">Region</Label>
                  <Select value={cfgForm.region} onValueChange={v => setCfgForm({ ...cfgForm, region: v })}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white text-sm" data-testid="spaces-region"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10 text-white">{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-500 text-xs">Bucket name</Label><Input className="bg-black/40 border-white/10 text-white text-sm" value={cfgForm.bucket} onChange={e => setCfgForm({ ...cfgForm, bucket: e.target.value })} data-testid="spaces-bucket" /></div>
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500" disabled={busy === "cfg" || !isAdmin} onClick={saveSpacesCfg} data-testid="spaces-connect-btn">
                {busy === "cfg" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}Connect Spaces
              </Button>
              {!isAdmin && <p className="text-[10px] text-zinc-600">Only a full admin can configure Spaces.</p>}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-black/40 rounded px-3 py-2">
                <p className="text-xs text-zinc-300">Connected: <span className="font-mono text-blue-300">{spaces.bucket}</span> @ {spaces.region} <span className="text-zinc-600">(key {spaces.access_key_preview})</span></p>
                {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-500 hover:text-red-400" onClick={removeSpacesCfg} data-testid="spaces-disconnect-btn">Disconnect</Button>}
              </div>
              {objects.length === 0 ? <p className="text-sm text-zinc-500">No objects in Spaces yet. Use "Spaces" or "Cold" on an archive above to upload.</p> :
                objects.map(o => (
                  <div key={o.key} className="flex items-center gap-3 px-3 py-2 bg-white/5 border border-white/10 rounded-lg" data-testid={`spaces-obj-${o.key}`}>
                    {o.storage_class === "GLACIER" ? <Snowflake className="w-4 h-4 text-cyan-300 shrink-0" /> : <Cloud className="w-4 h-4 text-blue-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{o.key}</p>
                      <p className="text-[10px] text-zinc-500">{o.size_human} · {o.storage_class}{o.storage_class === "GLACIER" ? " (cold)" : ""}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-white/10 text-white" onClick={() => presign(o.key)} data-testid={`presign-${o.key}`}><ExternalLink className="w-3 h-3 mr-1" />Link</Button>
                    {isAdmin && <button className="text-zinc-500 hover:text-red-400 p-1" onClick={() => delObject(o.key)} data-testid={`del-obj-${o.key}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))
              }
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
